import { Box, Button, Chip, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import DoneOutlinedIcon from '@mui/icons-material/DoneOutlined';

const NotificationPanel = ({
    title,
    rows,
    footerText = 'Ver mais',
    onPrevPage,
    onNextPage,
    disablePrev = false,
    disableNext = false,
    pageLabel = '',
    onMarkSeen,
    onRowClick,
    view = 'unseen',
    onChangeView
}) => {
    const isHistoryView = view === 'seen';

    const renderNotificationMarker = (row) => {
        if (row.type === 'ALERT') {
            return (
                <Box component="img" src="/assets/logos-svg/alert.svg" alt="Alerta" sx={{ width: 18, height: 18, display: 'block' }} />
            );
        }

        if (row.type === 'MISSED_CALL') {
            return (
                <Box
                    component="img"
                    src="/assets/logos-svg/missedCall.svg"
                    alt="Chamada perdida"
                    sx={{ width: 18, height: 18, display: 'block' }}
                />
            );
        }

        if (row.type === 'APPT_REMINDER') {
            return (
                <Box
                    component="img"
                    src="/assets/logos-svg/agendaRed.svg"
                    alt="Lembrete de agendamento"
                    sx={{ width: 18, height: 18, display: 'block' }}
                />
            );
        }

        if (row.type === 'NEW_DOCUMENT_UPLOAD') {
            return (
                <Box
                    component="img"
                    src="/assets/logos-svg/uploadRed.svg"
                    alt="Novo documento"
                    sx={{ width: 18, height: 18, display: 'block' }}
                />
            );
        }

        return <FiberManualRecordIcon sx={{ fontSize: 14, color: '#6f6f6f' }} />;
    };

    return (
        <Paper
            variant="elevation=0"
            sx={{
                p: { xs: 1.8, md: 2.2 },
                bgcolor: '#ffffff',
                borderRadius: '20px',
                boxShadow: 'none'
            }}
        >
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems={{ xs: 'stretch', md: 'center' }}
                justifyContent="space-between"
                spacing={1.2}
                mb={1.8}
            >
                <Typography variant="h6" fontWeight={700} color="#3D3D3D">
                    {title}
                </Typography>

                <Stack direction="row" spacing={1}>
                    <Tooltip title="Mostrar notificações por ler">
                        <Button
                            variant="outlined"
                            onClick={() => onChangeView?.('unseen')}
                            sx={{
                                minHeight: 36,
                                px: 1.8,
                                borderRadius: '12px',
                                textTransform: 'none',
                                fontWeight: 700,
                                borderColor: view === 'unseen' ? '#D63731' : '#D9D9D9',
                                color: view === 'unseen' ? '#D63731' : '#3D3D3D',
                                bgcolor: view === 'unseen' ? 'rgba(214, 55, 49, 0.05)' : '#FFFFFF',
                                '&:hover': {
                                    borderColor: view === 'unseen' ? '#D63731' : '#CFCFCF',
                                    bgcolor: view === 'unseen' ? 'rgba(214, 55, 49, 0.08)' : '#F7F7F7'
                                }
                            }}
                        >
                            Por ler
                        </Button>
                    </Tooltip>
                    <Tooltip title="Mostrar histórico de notificações">
                        <Button
                            variant="outlined"
                            onClick={() => onChangeView?.('seen')}
                            sx={{
                                minHeight: 36,
                                px: 1.8,
                                borderRadius: '12px',
                                textTransform: 'none',
                                fontWeight: 700,
                                borderColor: view === 'seen' ? '#D63731' : '#D9D9D9',
                                color: view === 'seen' ? '#D63731' : '#3D3D3D',
                                bgcolor: view === 'seen' ? 'rgba(214, 55, 49, 0.05)' : '#FFFFFF',
                                '&:hover': {
                                    borderColor: view === 'seen' ? '#D63731' : '#CFCFCF',
                                    bgcolor: view === 'seen' ? 'rgba(214, 55, 49, 0.08)' : '#F7F7F7'
                                }
                            }}
                        >
                            Histórico
                        </Button>
                    </Tooltip>
                </Stack>
            </Stack>

            <Paper
                sx={{
                    px: 2.2,
                    py: 1.1,
                    mb: 1.1,
                    display: 'grid',
                    gridTemplateColumns: isHistoryView ? '24px 1.25fr 2.1fr auto' : '24px 1.15fr 1.9fr auto 48px',
                    gap: 1.2,
                    alignItems: 'center',
                    bgcolor: '#3D3D3D',
                    color: '#fff',
                    borderRadius: '10px',
                    boxShadow: 'none'
                }}
            >
                <Box />
                <Typography variant="caption" fontWeight={600}>
                    Tipo
                </Typography>
                <Typography variant="caption" fontWeight={600}>
                    Descrição
                </Typography>
                <Typography variant="caption" fontWeight={600} textAlign="center">
                    Prioridade
                </Typography>
                {!isHistoryView ? (
                    <Typography variant="caption" fontWeight={600} textAlign="center">
                        Ação
                    </Typography>
                ) : null}
            </Paper>

            <Stack spacing={1}>
                {rows.length === 0 ? (
                    <Paper
                        sx={{
                            px: 2.2,
                            py: 1.35,
                            minHeight: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            bgcolor: '#ECECEC',
                            borderRadius: '12px',
                            boxShadow: 'none'
                        }}
                    >
                        <Typography variant="body2" fontWeight={500}>
                            Sem notificações
                        </Typography>
                    </Paper>
                ) : null}

                {rows.map((row) => (
                    <Paper
                        key={row.id}
                        onClick={() => onRowClick?.(row)}
                        sx={{
                            px: 2.2,
                            py: 1.2,
                            minHeight: 55,
                            display: 'grid',
                            gridTemplateColumns: isHistoryView ? '24px 1.25fr 2.1fr auto' : '24px 1.15fr 1.9fr auto 48px',
                            alignItems: 'center',
                            gap: 1.2,
                            bgcolor: '#ECECEC',
                            borderRadius: '12px',
                            boxShadow: 'none',
                            cursor: onRowClick ? 'pointer' : 'default',
                            transition: 'background-color 0.2s ease',
                            '&:hover': onRowClick
                                ? {
                                      bgcolor: '#E4E4E4'
                                  }
                                : undefined
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            {renderNotificationMarker(row)}
                        </Box>
                        <Typography variant="body2" fontWeight={row.highlight ? 700 : 600} color="#3D3D3D" textAlign="left">
                            {row.category}
                        </Typography>
                        <Typography variant="body2" fontWeight={600} color="text.secondary" textAlign="left">
                            {row.description}
                        </Typography>
                        {row.status ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Chip size="small" label={row.status} sx={{ bgcolor: '#f5c4c4', color: '#8f2a2a', fontWeight: 600 }} />
                            </Box>
                        ) : (
                            <Box />
                        )}
                        {!isHistoryView ? (
                            <Tooltip title="Marcar como vista">
                                <IconButton
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onMarkSeen?.(row);
                                    }}
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        justifySelf: 'center',
                                        bgcolor: '#d8d8d8',
                                        color: '#000000',
                                        '&:hover': {
                                            bgcolor: '#a0a0a0'
                                        }
                                    }}
                                >
                                    <DoneOutlinedIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                            </Tooltip>
                        ) : null}
                    </Paper>
                ))}
            </Stack>

            {onPrevPage || onNextPage ? (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5, gap: 0.5 }}>
                    <Tooltip title="Página anterior">
                        <span>
                            <IconButton size="small" onClick={onPrevPage} disabled={disablePrev}>
                                <KeyboardArrowLeftIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ minWidth: 28, textAlign: 'center', alignSelf: 'center' }}
                    >
                        {pageLabel}
                    </Typography>
                    <Tooltip title="Página seguinte">
                        <span>
                            <IconButton size="small" onClick={onNextPage} disabled={disableNext}>
                                <KeyboardArrowRightIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            ) : rows.length > 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                        {footerText}
                    </Typography>
                </Box>
            ) : null}
        </Paper>
    );
};

export default NotificationPanel;
